import { DayjsProperty } from '@/lib/jackson'
import { formatValueStringToLabel } from '@/utils/helper'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'
import type { PackageClass, PackageQuotaResponse } from '../package/model'
import { PackageTypeEnum } from '../package/model'

/** Quota at membership level (from GET /memberships) */
export interface MembershipQuota {
  id: number
  packageQuotaId: number
  remainingUses: number
  isUnlimit?: boolean
  packageQuota: PackageQuotaResponse
  userMembershipId: number
  quotaResetType?: string
}

export enum MEMBER_TYPE {
  ALL = 'ALL',
  MEMBER = 'MEMBER',
  SESSION_MEMBER = 'SESSION_MEMBER',
  DROP_IN = 'DROP_IN',
}

export interface UserMembershipParam {
  userId: string
  includeExpireDate: boolean
  packageType?: PackageTypeEnum
}

export interface MemberShipPayload {
  startDate: string
  expireDate: string
  note?: string
}

export enum MemberStatusFilter {
  ALL = 'all',
  BOOKED = 'booked',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NOT_PARTICIPATED = 'not_participated',
}

@Serializable()
export class Member extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('membershipId')
  membershipId?: number

  @JsonProperty('code')
  code!: string

  @DayjsProperty()
  expireDate?: Dayjs

  @DayjsProperty()
  latestCheckedInAt?: Dayjs

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('dateOfBirth')
  dateOfBirth?: string | null

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('memberType')
  memberType!: string

  @JsonProperty('status')
  status?: string // legacy, kept for backward compat

  @JsonProperty('memberStatus')
  memberStatus?: string

  @JsonProperty('packageStatus')
  packageStatus?: string

  @JsonProperty('accountStatus')
  accountStatus?: string

  @JsonProperty('totalCheckIn')
  totalCheckIn!: number

  @JsonProperty('saleOwner')
  saleOwner!: PersonalNameInfo

  @JsonProperty('creditBalance')
  creditBalance!: {
    totalCredits: number
    updatedAt: string
  }

  @JsonProperty('swimmingPackageName')
  swimmingPackageName?: string | null

  @JsonProperty('swimmingRemainingSessions')
  swimmingRemainingSessions?: number | null

  @JsonProperty('swimmingUnlimited')
  swimmingUnlimited?: boolean

  @JsonProperty('freezeUntil')
  freezeUntil?: string | null

  /** Name of the package this member holds — the same membership `memberType`
   *  and `packageStatus` are derived from. */
  @JsonProperty('packageName')
  packageName?: string | null

  @JsonProperty('packageTypeName')
  packageTypeName?: string | null

  /** Every package the member can use today: 'Name (remaining/total)' joined by '; '. */
  @JsonProperty('activePackages')
  activePackages?: string | null

  @JsonProperty('checkins30d')
  checkins30d?: number

  // Price, remaining sessions, unlimited-ness and purchase source are NOT on
  // this endpoint: GET /members is ungated, so its payload reaches every
  // authenticated role. They are export-only columns (members CSV / sale
  // managed-members CSV+Excel).

  public option() {
    return {
      label: `${this.code} ${this.firstName} ${this.lastName}`,
      code: this.code,
      value: this.id,
    }
  }
  public optionWithMemberType() {
    let memberTypeLabel = ''
    const memberType = this.memberType as MEMBER_TYPE | 'SESSION'
    if (memberType === MEMBER_TYPE.DROP_IN) {
      memberTypeLabel = 'Drop In'
    } else if (memberType === MEMBER_TYPE.SESSION_MEMBER || memberType === 'SESSION') {
      memberTypeLabel = 'Session Member'
    } else if (memberType === MEMBER_TYPE.MEMBER) {
      memberTypeLabel = 'Member'
    }

    return {
      label: `${this.code} ${this.firstName} ${this.lastName} (${this.nickname}) | ${memberTypeLabel}`,
      code: this.code,
      value: this.id,
    }
  }
  public DataForTable() {
    const now = dayjs().local()
    const exp = this?.expireDate ? dayjs(this.expireDate).local() : null
    const diffDays = exp ? exp.startOf('day').diff(now.startOf('day'), 'day') + 1 : undefined

    return {
      id: this.id,
      memberId: this.code,
      status: this.status,
      // Pass undefined through when the backend omits these — the badge
      // components render an explicit "—" pill for missing data instead of
      // silently picking a state.
      memberStatus: this.memberStatus,
      packageStatus: this.packageStatus,
      accountStatus: this.accountStatus,
      memberName: `${this?.firstName} ${this?.lastName}`,
      memberType: formatValueStringToLabel(this.memberType),
      expireDate: {
        date: this?.expireDate?.format('DD MMMM YYYY') ?? '-',
        // Abbreviated month variant for narrow list columns ("20 Sep 2026").
        dateShort: this?.expireDate?.format('DD MMM YYYY') ?? '-',
        count: diffDays === undefined || diffDays <= 0 ? undefined : `(In ${diffDays} Days)`,
        // Raw signed day diff so renderers can choose warn / danger styling
        // without re-parsing the string (positive = future, ≤ 0 = expired).
        diffDays,
      },
      totalCheckIn: this.totalCheckIn,
      packageName: this.packageName ?? '-',
      activePackages: this.activePackages ?? '',
      saleOwner: this.saleOwner ? `${this.saleOwner?.firstName} ${this.saleOwner?.lastName}` : '-',
      freezeUntil: this.freezeUntil ? dayjs(this.freezeUntil).format('DD MMMM YYYY') : null,
    }
  }
}

/**
 * Tooltip for the member-list Package cell. The cell names the package the
 * member holds; the tooltip lists every package they can use — worth showing
 * only when there is more than one, since a single-package member would just
 * see the cell repeated. Segments are joined by '; ' server-side.
 */
export const memberPackagesTooltip = (row: { activePackages?: string | null }): string => {
  const active = row.activePackages?.trim() ?? ''
  return active.includes(';') ? active : ''
}

@Serializable()
export class CheckInSummaryUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('lastCheckIn')
  lastCheckIn!: any

  @JsonProperty('profileImage')
  profileImage!: any

  @JsonProperty('memberType')
  memberType!: any
}

@Serializable()
export class PersonalNameInfo extends Fromable {
  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string
}

@Serializable()
export class CheckInSummaryActivity extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('type')
  type!: string

  @JsonProperty('title')
  title!: string

  @JsonProperty('name')
  name!: string

  @JsonProperty('startTime')
  startTime!: string

  @JsonProperty('endTime')
  endTime!: string

  @JsonProperty('location')
  location!: string

  @JsonProperty('instructor')
  instructor!: PersonalNameInfo

  @JsonProperty('status')
  status!: string
}

@Serializable()
export class CheckInSummaryMonthly extends Fromable {
  @JsonProperty('checkIn')
  checkIn!: number

  @JsonProperty('personalTrainer')
  personalTrainer!: number

  @JsonProperty('swimming')
  swimming!: number
}

@Serializable()
export class CheckInSummaryAllPackage extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('type')
  type!: string

  @JsonProperty('status')
  status!: string

  @JsonProperty('description')
  description!: string

  @JsonProperty('startDate')
  startDate!: string

  @JsonProperty('endDate')
  endDate!: any

  @JsonProperty('remainingCredits')
  remainingCredits!: number

  @JsonProperty('totalCredits')
  totalCredits!: number

  /**
   * QTY-2048: lets the check-in sheet recognise an entry-count package, so
   * `remainingCredits` can be rendered as "Remaining N uses" only where that
   * reads correctly. Absent on backends predating the feature.
   */
  @JsonProperty({ path: 'packageTypeId', required: false })
  packageTypeId?: number

  @JsonProperty({ path: 'unitType', required: false })
  unitType?: 'SESSIONS' | 'TIMES'
}

@Serializable()
export class CheckInSummary extends Fromable {
  @JsonProperty('user')
  user!: CheckInSummaryUser

  @JsonProperty('todaysActivity')
  todaysActivity!: CheckInSummaryActivity[]

  @JsonProperty('monthlySummary')
  monthlySummary!: CheckInSummaryMonthly

  @JsonProperty('recentActivity')
  recentActivity!: CheckInSummaryActivity[]

  @JsonProperty('allPackages')
  allPackages!: CheckInSummaryAllPackage[]
}

/** The bundle an entitlement was sold inside, as returned by GET /memberships. */
@Serializable()
export class MembershipBundleSummary extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name: string = ''

  @JsonProperty('price')
  price: number = 0

  @JsonProperty('description')
  description?: string
}

/**
 * What this package actually cost and which receipt says so (QTY-2074).
 *
 * The BE reads it off the receipt line that created the membership, so both
 * figures are frozen at the moment of sale — editing the package in Packages
 * Management does not move them. Absent entirely (null) for packages with no
 * receipt behind them: comped, transferred, and rows migrated from V1. Those
 * render "-"; never fall back to `package.price`, which is the live master
 * price and the very thing this replaces.
 *
 * Both amounts are in SATANG, like `MembershipData.joiningFee` and unlike
 * `MembershipPackage.price` — pass them through `satangToBaht` before display.
 */
@Serializable()
export class MembershipPurchase extends Fromable {
  /** What the member paid for this package, in satang. */
  @JsonProperty('pricePaid')
  pricePaid?: number | null

  /**
   * List price at time of sale, in satang. For a package sold inside a bundle
   * this is its share of the bundle's selling price (QTY-2071), not the
   * component package's own list price.
   */
  @JsonProperty('priceFull')
  priceFull?: number | null

  @JsonProperty('receiptId')
  receiptId?: number | null

  /** As printed on the receipt: tax invoice no., or the legacy code. */
  @JsonProperty('receiptNo')
  receiptNo?: string | null
}

/** B-916: a PT as the trainer-change endpoints name one. */
export interface TrainerChangePerson {
  id: number
  firstName?: string | null
  lastName?: string | null
  nickname?: string | null
}

/** GET /memberships/:id/trainer-change/candidates row. */
export interface TrainerChangeCandidate extends TrainerChangePerson {
  code?: string | null
  imageUrl?: string | null
  memberCount: number
}

/** GET /memberships/:id/trainer-change/preview — what the change would cancel. */
export interface TrainerChangePreview {
  from: TrainerChangePerson | null
  to: TrainerChangePerson | null
  cancelSessions: {
    id: number
    startTime: string
    endTime: string
    trainer?: TrainerChangePerson | null
  }[]
}

/** POST /memberships/:id/trainer-change result. */
export interface TrainerChangeResult {
  from: TrainerChangePerson | null
  to: TrainerChangePerson | null
  changedAt: string
  cancelledBookingIds: number[]
  failedBookingIds: number[]
}

@Serializable()
export class MembershipData extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('startDate')
  startDate!: string

  @JsonProperty('expireDate')
  expireDate!: string

  @JsonProperty('remainingUses')
  remainingUses!: number

  @JsonProperty('joiningFee')
  joiningFee!: number | null

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('branchId')
  branchId!: any

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('userId')
  userId!: number

  @JsonProperty('user')
  user?: MembershipPerson

  @JsonProperty('owner')
  owner?: MembershipPerson | null

  @JsonProperty('sale')
  sale!: MembershipPerson

  @JsonProperty('trainer')
  trainer?: MembershipPerson | null

  /** B-916: the PT this package was last changed from; null if never changed. */
  @JsonProperty('trainerChangedFrom')
  trainerChangedFrom?: MembershipPerson | null

  @JsonProperty('trainerChangedAt')
  trainerChangedAt?: string | null

  @JsonProperty('package')
  package!: MembershipPackage

  @JsonProperty('quotas')
  quotas?: MembershipQuota[]

  @JsonProperty('note')
  note?: string

  /** Price paid, list price at sale time and receipt number (QTY-2074). */
  @JsonProperty({ path: 'purchase', type: MembershipPurchase })
  purchase?: MembershipPurchase | null

  /** Set when this entitlement was spawned by a bundle sale. */
  @JsonProperty('bundleId')
  bundleId?: number | null

  /** Identifies one bundle purchase, so a renewal groups apart from the original. */
  @JsonProperty('bundlePurchaseId')
  bundlePurchaseId?: string | null

  @JsonProperty('bundle')
  bundle?: MembershipBundleSummary | null

  /** New vs renewal at purchase time; stamped by the server, never changes. */
  @JsonProperty('purchaseSource')
  purchaseSource?: MembershipPurchaseSource | null

  /** Given away rather than sold (receipt carries a matching 100% discount). */
  @JsonProperty('isComplimentary')
  isComplimentary?: boolean

  /** Family Package (QTY-2028): set when sold as a family package; `userId` is the Primary. */
  @JsonProperty('familySharingMode')
  familySharingMode?: FamilySharingMode | null

  /** Eligible roster (Primary first). Masked server-side for non-Primary member viewers. */
  @JsonProperty('familyMembers')
  familyMembers?: FamilyMember[]

  public DataForTable() {
    return {
      memberId: this.id,
      status: this.isActive,
      memberName: `${this.user?.firstName} ${this.user?.lastName}`,
      memberType: this?.package?.name,
      expireDate: this.expireDate,
      totalCheckIn: this.remainingUses,
      saleOwner: this.owner ? `${this.owner?.firstName} ${this.owner?.lastName}` : '-',
    }
  }
}

@Serializable()
export class MembershipPerson extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('imageUrl')
  imageUrl!: any

  @JsonProperty('code')
  code!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('email')
  email!: any

  @JsonProperty('phone')
  phone!: any
}

@Serializable()
export class MembershipPackage extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty('credit')
  credit!: number

  @JsonProperty('packageType')
  packageType!: MembershipPackageType

  @JsonProperty('description')
  description!: string

  @JsonProperty('price')
  price!: number

  @JsonProperty('usageLimit')
  usageLimit!: number

  @JsonProperty('accessType')
  accessType?: string

  @JsonProperty('packageQuotas')
  packageQuotas?: PackageQuotaResponse[]

  @JsonProperty('packageClasses')
  packageClasses?: PackageClass[]

  // Branch Access on member package cards (QTY-1851). `branches` is the
  // flattened allow-list the BE derives from the PackageBranch pivot; it is
  // empty for all-branches packages.
  @JsonProperty('isAllBranches')
  isAllBranches?: boolean

  @JsonProperty('branches')
  branches?: { id: number; name: string }[]

  /** Family-capable package (QTY-2027). */
  @JsonProperty('isFamily')
  isFamily?: boolean
}

@Serializable()
export class MembershipPackageType extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('nameEn')
  nameEn!: string

  @JsonProperty('nameTh')
  nameTh!: string
}

@Serializable()
export class MemberOption extends Fromable {
  @JsonProperty('slug')
  slug!: string

  @JsonProperty('name')
  name!: string
}

/**
 * Member-level standing filter (QTY-1606 tabs).
 * Independent from package lifecycle.
 */
export enum MEMBER_STATUS {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  BANNED = 'BANNED',
  FREEZE = 'FREEZE',
}

/**
 * Package lifecycle filter (QTY-1606 dropdown).
 *
 * QTY-2078: a session package whose sessions have run out is EXPIRED — there
 * is no separate "Fully Used" state.
 */
export enum PACKAGE_STATUS {
  ALL = 'ALL',
  ON_GOING = 'ON_GOING',
  NOT_STARTED = 'NOT_STARTED',
  EXPIRED = 'EXPIRED',
}

export enum ACCOUNT_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface UpdateAccountStatusPayload {
  status: ACCOUNT_STATUS
  acknowledged?: boolean
}

export interface MemberAccountStatusInfo {
  status: ACCOUNT_STATUS
  hasActivePackage: boolean
  usedHeadcount: number
  headcountLimit: number
}

export enum EmergencyRelationship {
  SPOUSE_PARTNER = 'SPOUSE_PARTNER',
  PARENT = 'PARENT',
  SIBLING = 'SIBLING',
  FRIEND = 'FRIEND',
  OTHER = 'OTHER',
}

@Serializable()
export class CreateMemberEmergencyContact extends Fromable {
  @JsonProperty('name')
  name!: string

  @JsonProperty('phone')
  phone!: string

  @JsonProperty('relationship')
  relationship!: EmergencyRelationship | string
}

@Serializable()
export class CreateMemberPayload extends Fromable {
  @JsonProperty('code')
  code?: string

  @JsonProperty('avatar')
  avatar?: File

  @JsonProperty('uid')
  uid?: string

  @JsonProperty('citizenId')
  citizenId!: string

  @JsonProperty('saleOwnerId')
  saleOwnerId!: number

  @JsonProperty('saleOwnerName')
  saleOwnerName?: string

  @JsonProperty('imageUrl')
  imageUrl!: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('dateOfBirth')
  dateOfBirth!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('bloodType')
  bloodType!: string

  @JsonProperty('height')
  height!: number

  @JsonProperty('weight')
  weight!: number

  @JsonProperty('occupation')
  occupation!: string

  @JsonProperty('marriedStatus')
  marriedStatus!: string

  @JsonProperty('address')
  address!: string

  @JsonProperty('zipcode')
  zipcode!: string

  @JsonProperty('provinceId')
  provinceId!: number

  @JsonProperty('districtId')
  districtId!: number

  @JsonProperty('subDistrictId')
  subDistrictId!: number

  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone!: string

  // QTY-1858: required (true) by the API whenever phone is omitted — staff
  // acknowledgement that the member can't register on LINE OA without one.
  @JsonProperty('noPhoneAcknowledged')
  noPhoneAcknowledged?: boolean

  @JsonProperty('emergencyContact')
  emergencyContact!: CreateMemberEmergencyContact

  @JsonProperty('referralChannel')
  referralChannel!: string

  @JsonProperty('referralChannelOther')
  referralChannelOther!: string
}

export interface MemberShipPaymentDetails {
  paymentMethod: string
  isInstallment: boolean
  installmentPeriod?: number
  installmentRate?: number
  bankFeeId?: number
  /** Serial of the POS machine this sale was rung up on. */
  posId?: string
}

export enum RECURRING_COLLECTION_MODE {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
}

export interface RecurringPayment {
  numberOfInstallments: number
  nextDueDate: string
  collectionMode?: RECURRING_COLLECTION_MODE
  note?: string | null
}

export enum MEMBERSHIP_INSTALLMENT_STATUS {
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  FAILED = 'FAILED',
  WAIVED = 'WAIVED',
}

export enum MEMBERSHIP_PLAN_STATUS {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export interface MembershipInstallmentData {
  id: number
  sequence: number
  dueDate: string
  amount: number
  status: MEMBERSHIP_INSTALLMENT_STATUS
  paidAt: string | null
  paidReceiptId: number | null
}

export interface MembershipPaymentPlanData {
  id: number
  description: string | null
  totalAmount: number
  numberOfInstallments: number
  installmentAmount: number
  frequency: string
  startDate: string
  collectionMode: RECURRING_COLLECTION_MODE
  status: MEMBERSHIP_PLAN_STATUS
  createdAt: string
  installments: MembershipInstallmentData[]
}

export interface CollectInstallmentPayload {
  paymentMethod: string
  bankFeeId?: number
  /** Serial of the POS machine this payment was collected on. */
  posId?: string
}

export interface CollectBulkPayload {
  installmentIds: number[]
  paymentMethod: string
  bankFeeId?: number
  /** Serial of the POS machine these payments were collected on. */
  posId?: string
}

export interface CollectBulkResult {
  successCount: number
  failedCount: number
  failures: { id: number; reason: string }[]
}

/**
 * The three actionable buckets of the recurring-payments worklist. PENDING is
 * not-yet-due, OVERDUE covers both the flipped rows and pending-past-due, FAILED
 * is a collection that was recorded as failed. PAID/WAIVED are never listed.
 */
export type DueInstallmentStatusFilter = 'PENDING' | 'OVERDUE' | 'FAILED'

/** Query params for the recurring-payments worklist (`GET /memberships/installments/due`). */
export interface DueInstallmentQuery {
  status?: DueInstallmentStatusFilter
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  query?: string
  /** Inclusive lower bound on the due date, as a `YYYY-MM-DD` calendar date. */
  dueFrom?: string
  /** Inclusive upper bound on the due date, as a `YYYY-MM-DD` calendar date. */
  dueTo?: string
}

export interface DueInstallmentRow {
  id: number
  sequence: number
  dueDate: string
  amount: number
  status: MEMBERSHIP_INSTALLMENT_STATUS
  planId: number
  planStatus: MEMBERSHIP_PLAN_STATUS
  numberOfInstallments: number
  description: string | null
  memberId: number
  memberName: string
  memberCode: string
  membershipSuspended: boolean
}

export interface MemberShipTaxInvoiceAddress {
  firstName?: string | null
  lastName?: string | null
  companyName?: string | null
  branch?: string | null
  taxNo: string
  // Passport support for foreign individuals.
  idType?: string | null
  country?: string | null
  addressLine: string
  postalCode: string
  provinceId: number
  districtId: number
  subDistrictId: number
}

/** How a sale is classified: a first purchase vs a renewal / upsell. */
export type MembershipPurchaseSource = 'NEW' | 'EXISTING'

export interface MemberShipPackage {
  packageId: number
  startDate: string
  expireDate: string
  saleId: number
  ownerId?: number
  discount: number
  note: string
  trainerId?: number
  joiningFee?: number
  /** Set when this package is a component of a bundle sale. */
  bundleId?: number
  /** Staff's New/Renew call for this line; omitted = the server infers it. */
  purchaseSource?: MembershipPurchaseSource
  /** Giveaway — the server prices the line as a 100% discount. */
  isComplimentary?: boolean
  /** Family Package (QTY-2028): only for packages flagged isFamily. */
  family?: { sharingMode: FamilySharingMode; memberUserIds: number[] }
}

export type FamilySharingMode = 'SHARED_POOL' | 'PER_MEMBER_ALLOCATION'

/** One eligible member on a sold family package (QTY-2029). Numbers are null
 *  when the server masked them for a non-Primary viewer. */
export interface FamilyMember {
  id: number
  userMembershipId: number
  userId: number
  isPrimary: boolean
  isEligible: boolean
  allocatedUses: number | null
  usedUses: number | null
  remainingUses?: number | null
  removedAt?: string | null
  createdAt: string
  user: {
    id: number
    code: string
    firstName?: string | null
    lastName?: string | null
    nickname?: string | null
  }
}

/**
 * One member's target allocation in a batch edit (QTY-2087). `allocatedUses` is
 * the total the member should end up with, used sessions included — not a
 * delta. `expectedAllocatedUses` is what the screen was showing, so the server
 * can reject an edit that would silently overwrite someone else's save.
 */
export interface FamilyAllocationInput {
  userId: number
  allocatedUses: number
  expectedAllocatedUses?: number
}

export interface FamilyTotals {
  isUnlimited: boolean
  total: number | null
  remaining: number | null
  allocated: number | null
  unallocated: number | null
  used: number
}

export interface FamilyPackageEvent {
  id: number
  eventType:
    | 'MEMBER_ADDED'
    | 'MEMBER_REMOVED'
    | 'MEMBER_ENABLED'
    | 'MEMBER_DISABLED'
    | 'ALLOCATION_TOP_UP'
    | 'ALLOCATION_ADJUSTED'
    | 'MODE_SWITCHED'
  actorUserId?: number | null
  actor?: FamilyMember['user'] | null
  targetUserId?: number | null
  target?: FamilyMember['user'] | null
  fromMode?: FamilySharingMode | null
  toMode?: FamilySharingMode | null
  amount?: number | null
  createdAt: string
}

export interface FamilyPackageDetail {
  membershipId: number
  sharingMode: FamilySharingMode
  primary: FamilyMember['user']
  totals: FamilyTotals
  members: FamilyMember[]
  events: FamilyPackageEvent[]
  /** QTY-2087: false once the package is inactive, expired or currently
   *  frozen — the allocation editor is disabled rather than failing on save. */
  allocationsEditable?: boolean
}

export interface CreateMemberShipPackagePayload {
  userId: number
  packages: MemberShipPackage[]
  paymentDetails?: MemberShipPaymentDetails
  recurring?: RecurringPayment | null
  isTaxInvoiceRequired?: boolean
  taxInvoiceAddress?: MemberShipTaxInvoiceAddress | null
}

export interface CreateMemberShipPackageResponse {
  success: boolean
  membershipIds: number[]
}

@Serializable()
export class EditPersonalProfilePayload extends Fromable {
  @JsonProperty('avatar')
  avatar?: File

  @JsonProperty('uid')
  uid?: string

  @JsonProperty('citizenId')
  citizenId!: string

  @JsonProperty('saleOwnerName')
  saleOwnerName?: string

  @JsonProperty('saleOwnerId')
  saleOwnerId?: number

  @JsonProperty('imageUrl')
  imageUrl?: string

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string

  @JsonProperty('dateOfBirth')
  dateOfBirth!: string

  @JsonProperty('gender')
  gender!: string

  @JsonProperty('bloodType')
  bloodType!: string

  @JsonProperty('height')
  height?: number

  @JsonProperty('weight')
  weight?: number

  @JsonProperty('occupation')
  occupation!: string

  @JsonProperty('marriedStatus')
  marriedStatus!: string

  @JsonProperty('address')
  address!: string

  @JsonProperty('zipcode')
  zipcode!: string

  @JsonProperty('provinceId')
  provinceId!: number

  @JsonProperty('districtId')
  districtId!: number

  @JsonProperty('subDistrictId')
  subDistrictId!: number

  @JsonProperty('referralChannel')
  referralChannel!: string

  @JsonProperty('referralChannelOther')
  referralChannelOther!: string

  /**
   * Allergies / product sensitivities. Surfaced as a warning in the Wellness & Spa booking flow,
   * so it is sent even when blank — clearing a wrong allergy note has to actually persist.
   */
  @JsonProperty('allergyNote')
  allergyNote?: string

  /**
   * Preferred spa therapist. `null` clears the preference — like `allergyNote` this is sent even
   * when empty, otherwise staff could never un-set a therapist who has left.
   */
  @JsonProperty('preferredTherapistUserId')
  preferredTherapistUserId?: number | null
}

@Serializable()
export class EditMemberContactPayload extends Fromable {
  @JsonProperty('email')
  email!: string

  @JsonProperty('phone')
  phone?: string

  @JsonProperty('phoneVerificationToken')
  phoneVerificationToken?: string

  @JsonProperty('removePhone')
  removePhone?: boolean

  @JsonProperty('emergencyContact')
  emergencyContact!: CreateMemberEmergencyContact
}

@Serializable()
export class EditMemberPackagePayload extends Fromable {
  @JsonProperty('startDate')
  startDate!: string

  @JsonProperty('expireDate')
  expireDate!: string

  @JsonProperty('note')
  note?: string
}

@Serializable()
export class MemberCheckInHistory extends Fromable {
  /** QTY-2049: identifies the row for undo. Absent on older backends. */
  @JsonProperty({ path: 'id', required: false })
  id?: number

  @JsonProperty('createdAt')
  createdAt!: string

  @JsonProperty('type')
  type!: string

  @JsonProperty({ path: 'checkOutAt', required: false })
  checkOutAt?: string | null

  @JsonProperty({ path: 'durationSeconds', required: false })
  durationSeconds?: number | null

  @JsonProperty({ path: 'branchId', required: false })
  branchId?: number | null

  @JsonProperty({ path: 'branchName', required: false })
  branchName?: string | null

  /**
   * QTY-2048: what this visit cost in package uses.
   *   -1   charged
   *    0   same-day repeat, let in free
   *   null no time-based package involved (unlimited membership, or a backend
   *        predating the feature)
   */
  @JsonProperty({ path: 'usesDelta', required: false })
  usesDelta?: number | null

  @JsonProperty({ path: 'remainingUsesAfter', required: false })
  remainingUsesAfter?: number | null

  @JsonProperty({ path: 'membershipId', required: false })
  membershipId?: number | null

  @JsonProperty({ path: 'packageName', required: false })
  packageName?: string | null

  /** QTY-2049: the charge was reversed. The visit itself still stands. */
  @JsonProperty({ path: 'isUndone', required: false })
  isUndone?: boolean

  @JsonProperty({ path: 'undoneAt', required: false })
  undoneAt?: string | null

  @JsonProperty({ path: 'undoneByName', required: false })
  undoneByName?: string | null

  /**
   * QTY-2049: whether this row's charge may still be reversed.
   *
   * Decided by the server — it depends on the club timezone and on every
   * check-in that day, while this client only holds one page of history.
   */
  @JsonProperty({ path: 'canUndo', required: false })
  canUndo?: boolean
}

@Serializable()
export class TrainingHistory extends Fromable {
  @JsonProperty('id')
  id!: number

  /** Branch the booking belongs to — pt-history is cross-branch, but the
   * status/cancel mutations are branch-scoped and need this as an override. */
  @JsonProperty('branchId')
  branchId?: number | null

  @JsonProperty('startTime')
  startTime!: string

  @JsonProperty('endTime')
  endTime!: string

  @JsonProperty('status')
  status!: string

  // Reason a manager recorded when manually changing this session's status.
  @JsonProperty('statusNote')
  statusNote?: string

  @JsonProperty('trainer')
  trainer!: {
    id: number
    firstName: string
    lastName: string
    nickname: string
    phone: string
  }

  @JsonProperty('service')
  service!: {
    id: number
    nameEn: string
    nameTh: string
    price?: number
  }
}

@Serializable()
export class ByUserType extends Fromable {
  @JsonProperty('price')
  price!: number

  @JsonProperty('capacity')
  capacity!: number

  @JsonProperty('bookingCount')
  bookingCount!: number

  @JsonProperty('queueCount')
  queueCount!: number
}

@Serializable()
export class TrainerMember extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string
}

@Serializable()
export class SwimmingHistory extends Fromable {
  @JsonProperty('bookingId')
  bookingId!: number

  @JsonProperty('scheduleId')
  scheduleId!: number

  @JsonProperty('classId')
  classId!: number

  @JsonProperty('className')
  className!: string

  @JsonProperty('startTime')
  startTime!: string

  @JsonProperty('endTime')
  endTime!: string

  @JsonProperty('bookingStatus')
  bookingStatus!: string

  @JsonProperty('trainer')
  trainer!: {
    id: number
    firstName: string
    lastName: string
  }

  @JsonProperty({ elementType: TrainerMember })
  trainers?: TrainerMember[]

  @JsonProperty('cancelledAt')
  cancelledAt!: string | null

  /**
   * How the booking came to be cancelled — ON_TIME / LATE / STAFF / SYSTEM.
   * `null` on rows that were never cancelled and on pre-migration cancels, so
   * the chip is skipped rather than guessed.
   */
  @JsonProperty('cancelKind')
  cancelKind?: string | null

  @JsonProperty('cancelReason')
  cancelReason?: string | null

  /** True when the cancel deliberately gave nothing back. */
  @JsonProperty('refundSkipped')
  refundSkipped?: boolean

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('branchName')
  branchName!: string

  @JsonProperty('location')
  location!: string

  @JsonProperty('roomId')
  roomId?: string | null

  @JsonProperty('byUserType')
  byUserType!: {
    MEMBER: ByUserType
    SESSION: ByUserType
    DROP_IN: ByUserType
  }
}

export interface MemberNote {
  id: number
  memberId: number
  editorId: number
  note: string | null
  createdAt: string
  updatedAt: string
  editor: MemberNoteEditor
  member: MemberNoteEditor
}

export interface MemberNoteEditor {
  id: number
  firstName: string
  lastName: string
  gender: string
}
export class AggregateCount {
  count!: number
}
export type AggregateCountResponse = {
  count: number
}

export type MemberSummaryResult = {
  totalMemberType: number
  totalSessionMemberType: number
  totalExpireSoon: number
  totalDropIn: number
}

export type MemberMonthlyLatestActivity = {
  month_year: string
  member_id: number
  member_code: string
  nickname: string | null
  member_full_name: string
  gender: string | null
  phone_number: string | null
  email: string | null
  member_created_at: string

  date_of_birth: string | null
  age_years: number | null

  member_type_code: string
  member_type_label: string

  month_start: string
  month_end: string

  latest_activity_name: string | null
  latest_activity_start_time: string | null
  latest_activity_end_time: string | null

  member_checkin_count_in_month: string
  next_expire_date: string | null
  next_expire_sale_full_name: string | null

  sale_owner_id: number | null
  sale_owner_full_name: string | null

  is_expire_in_30_days: number
  is_inactive_more_than_14_days: number

  latest_checkin_at: string | null

  monthly_spenders: string
  last_payment_at: string | null

  balance_credits_as_of_month_end: number
  total_credits_used_as_of_month_end: number
  total_credits_purchased_as_of_month_end: string
}

export type GenderDemographicsResult = {
  male: number
  female: number
  notSpecified: number
  total: number
}

export type AgeDemographicsResult = {
  range0_12: number
  range13_17: number
  range18_29: number
  range30_39: number
  range40_49: number
  range50_59: number
  range60_plus: number
}

export interface InactiveMember {
  member_id: number
  member_code: string
  member_full_name: string
  nickname: string | null

  member_type_label: string
  phone_number: string | null

  latest_checkin_at: string | null

  latest_activity_name: string | null
  latest_activity_start_time: string | null

  next_expire_sale_full_name: string | null
}

export type PaginationOptInactive = {
  page: number
  pageSize: number
  memberTypeCode?: 'MEMBER' | 'SESSION_MEMBER' | 'DROP_IN'
  saleFullName?: string
  saleOwnerId?: number
  fromDate?: string
  toDate?: string
}

export type PaginationResponseMember<T> = {
  data: T[]
  total: number
}

export interface TopCreditSpender {
  member_code: string
  firstname: string | null
  lastname: string | null
  nickname: string | null
  mobile_number: string | null
  member_type: 'member' | 'session_member' | null

  total_credits_used_as_of_month_end: number | null
}

export interface MemberDocumentStaff {
  id: number
  firstName: string
  lastName: string
}

export interface MemberDocument {
  id: string
  memberId: number
  uploadedByStaffId: number
  uploadedByStaff: MemberDocumentStaff
  filePath: string
  fileSize: number
  originalFileName: string
  isVisibleToMember: boolean
  description: string
  internalNote: string
  createdAt: string
  updatedAt: string
}

export interface CreateMemberDocumentPayload {
  file: File
  isVisibleToMember: boolean
  description: string
  internalNote: string
}

export interface UpdateMemberDocumentPayload {
  isVisibleToMember: boolean
  description: string
  internalNote: string
}

export interface DeleteMemberDocumentResponse {
  success: boolean
}

export interface SaleOwnerChangePreview {
  openOpportunityCount: number
  oldSaleOwnerId: number | null
  oldSaleOwnerName: string | null
  newSaleOwnerId: number
  newSaleOwnerName: string
}

/** One choice in the member list's Sales Owner filter (QTY-2073). */
export interface SaleOwnerOption {
  id: number
  firstName?: string | null
  lastName?: string | null
  nickname?: string | null
  /**
   * False only when the staff record is soft-deleted — the person has left.
   * The server decides it on `deletedAt` alone, NOT on branch or role
   * (backend #908): someone who moved branch, or who owns members without a
   * sales role, is ordinary staff and comes back true. So the "no longer
   * employed" label this drives means exactly that. Either way they stay
   * selectable, so their portfolio remains reviewable.
   */
  isCurrentStaff: boolean
}

export interface AdjustCreditBody {
  amount: number
  adjustmentType: 'INCREASE' | 'DECREASE'
}
